/**
 * <contribe-trigger> - goer donationslinjen i buy box'en til triggeren for
 * Contribe-appens "Learn more"-modal, praecis som badge-klikket paa det
 * nuvaerende site.
 *
 * Hvorfor den her form (maalt paa live-sitet 2026-09-22):
 *  - Contribe-appen injicerer <contribe-badge> (Lit, OPEN shadow DOM). Klik-
 *    handleren sidder IKKE paa host-elementet, men paa .badge-container inde i
 *    shadow root'en: `<div class="badge-container" @click="${this.openReadMore}">`.
 *  - openReadMore saetter open + pluginConfig paa <contribe-modal id="contribe-modal">,
 *    som app'en selv appender til <body>. pluginConfig ER badge-elementets
 *    fetchede config - uden en badge-instans paa siden kan modalen ikke fyldes.
 *  - Der findes INGEN offentlig API (intet window.contribe.open(), ingen event).
 *    Derfor videresender vi klikket til app'ens eget .badge-container frem for
 *    at reimplementere aabne-logikken. Sidegevinst: app'ens egen analytics-ping
 *    (selection: "read-more") fyrer uaendret.
 *  - Badgen sidder i en visuelt skjult host i wrapperen (app-embeddens
 *    badge_placement_selector -> .js-contribe-badge-host, placement "append").
 *    Den er ren API-bro; den synlige, klikbare tekst er vores egen oversatte
 *    product.donation_note, saa serveren bliver ved med at eje sproget.
 *
 * Progressive enhancement: uden JS - eller hvis Contribe er afinstalleret,
 * app-embedden slaaet fra eller placement-selectoren peger et andet sted hen -
 * bliver linjen staaende som det <p> serveren renderede. Vi opgraderer den
 * foerst til <button> naar badgen faktisk er der og har renderet.
 *
 * A11y: Contribes modal er en bar <div class="modal-frame"> - ingen role,
 * ingen aria-modal, luk-krydset er et <span> uden tabindex, ingen Escape og
 * ingen focus trap (maalt 2026-09-22). EAA/DE er i scope, saa vi laegger
 * kontrakten paa udefra: shadow root'en er open, og host'ens open-attribut
 * styrer alt (removeAttribute('open') lukker). Se #enhance.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Badgen ankommer i tre trin: app-embeddens MutationObserver indsaetter
// elementet, plugin.mjs definerer custom elementet, og Lit renderer foerst
// naar config er hentet. Selve indsaettelsen venter vi paa med en observer
// uden deadline (er app'en slaaet fra, koster den ingenting og kommer aldrig
// et svar); shadow root'ens foedsel kan derimod ikke observeres udefra, saa
// RENDER-trinnet poller bundet - 40 x 250ms = 10s, derefter giver vi op og
// linjen bliver et almindeligt afsnit.
const POLL_MS = 250;
const POLL_TRIES = 40;

class ContribeTrigger extends HTMLElement {
  connectedCallback() {
    // Elementet kan blive FLYTTET frem for gen-renderet (blok-reorder i
    // editoren), og saa koerer connectedCallback igen. Uden den her vagt ville
    // vi efterlade den foerste observer/timer koerende og kunne naa at
    // opgradere to gange - anden gang laeser den knappens tekst, som allerede
    // indeholder den skjulte hint-tekst.
    if (this.label && this.label.tagName === 'BUTTON') return;
    this.#teardownWatchers();
    this.label = this.querySelector('[data-contribe-label]');
    this.host = this.querySelector('[data-contribe-host]');
    if (!this.label || !this.host) return;
    this.#awaitBadge();
  }

  disconnectedCallback() {
    this.#release();
    this.#teardownWatchers();
  }

  #teardownWatchers() {
    if (this.hostObserver) {
      this.hostObserver.disconnect();
      this.hostObserver = null;
    }
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  // Trin 1: vent paa at app-embedden appender <contribe-badge> i vores host.
  #awaitBadge() {
    const existing = this.host.querySelector('contribe-badge');
    if (existing) {
      this.#awaitRender(existing, POLL_TRIES);
      return;
    }
    this.hostObserver = new MutationObserver(() => {
      const badge = this.host.querySelector('contribe-badge');
      if (!badge) return;
      this.hostObserver.disconnect();
      this.hostObserver = null;
      this.#awaitRender(badge, POLL_TRIES);
    });
    this.hostObserver.observe(this.host, { childList: true, subtree: true });
  }

  // Trin 2: vent paa at badgen har renderet sin .badge-container.
  #awaitRender(badge, tries) {
    if (this.#container(badge)) {
      this.#upgrade(badge);
      return;
    }
    if (tries <= 0) return;
    this.pollTimer = setTimeout(() => this.#awaitRender(badge, tries - 1), POLL_MS);
  }

  #container(badge) {
    return badge.shadowRoot ? badge.shadowRoot.querySelector('.badge-container') : null;
  }

  // Trin 3: erstat afsnittet med en knap. type='button' er kritisk - linjen
  // ligger inde i {% form 'product' %}, og en default-knap ville submitte
  // add-to-cart. Den skjulte hint-tekst gemmer den synlige etiket i det
  // tilgaengelige navn (WCAG 2.5.3 label in name).
  #upgrade(badge) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = this.label.className;
    button.setAttribute('aria-haspopup', 'dialog');
    button.textContent = this.label.textContent.trim();
    if (this.dataset.textOpen) {
      const hint = document.createElement('span');
      hint.className = 'visually-hidden';
      hint.textContent = ` ${this.dataset.textOpen}`;
      button.append(hint);
    }
    this.label.replaceWith(button);
    this.label = button;
    button.addEventListener('click', () => this.#open(badge));
  }

  #open(badge) {
    const container = this.#container(badge);
    if (!container) return;
    // App'ens egen aabne-vej: saetter open + pluginConfig paa #contribe-modal.
    container.click();
    // Synkron laesning er sikker: app'en opretter #contribe-modal ved import af
    // plugin.mjs, ikke ved foerste klik, og Lit renderer dens shadow root med
    // det samme - kun synligheden er CSS-gated bag :host([open]). Maalt paa
    // live-sitet 2026-09-22: element, shadow root OG .modal-close findes alle
    // foer det foerste klik. Aendrer app'en det, falder vi tilbage til dens
    // egen (ringere) modal frem for at kaste - se vagterne i #enhance.
    const modal = document.getElementById('contribe-modal');
    if (modal) this.#enhance(modal);
  }

  // Dialog-kontrakten laegges paa app'ens modal udefra. Alt herinde er
  // defensivt: rammer vi en fremtidig version uden .modal-close eller uden
  // open shadow root, falder vi tilbage til app'ens egen (ringere) opfoersel
  // i stedet for at kaste.
  #enhance(modal) {
    if (this.trapped) return;
    this.trapped = modal;

    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('tabindex', '-1');
    if (this.dataset.textDialog) modal.setAttribute('aria-label', this.dataset.textDialog);

    const close = modal.shadowRoot ? modal.shadowRoot.querySelector('.modal-close') : null;
    if (close && !close.dataset.a11yBound) {
      close.dataset.a11yBound = 'true';
      close.setAttribute('role', 'button');
      close.setAttribute('tabindex', '0');
      if (this.dataset.textClose) close.setAttribute('aria-label', this.dataset.textClose);
      close.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        close.click();
      });
    }

    // Capture, saa vi ser Escape/Tab foer app'en - tastatur-events fra shadow
    // DOM er composed og naar document.
    this.onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        modal.removeAttribute('open');
        return;
      }
      if (event.key === 'Tab') this.#trap(event, modal);
    };
    document.addEventListener('keydown', this.onKeydown, true);

    // App'en lukker ogsaa selv (kryds, klik udenfor) ved at fjerne open.
    // Observeren er den ENE sandhed om "nu er den lukket", saa fokus-retur
    // og oprydning sker uanset hvem der lukkede.
    this.openObserver = new MutationObserver(() => {
      if (modal.hasAttribute('open')) return;
      this.#release();
    });
    this.openObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });

    // preventScroll er IKKE valgfri her: app'en appender <contribe-modal> til
    // bunden af <body>, og host'en er et tomt inline-element paa det sted i
    // dokumentflowet - selve dialogen er en position:fixed efterkommer i dens
    // shadow root. Uden flaget scroller browseren den "tomme" host ind i
    // billedet, dvs. helt ned i bunden af siden, bag den fastlaaste modal.
    modal.focus({ preventScroll: true });
  }

  // Antagelse (maalt 2026-09-22): alle modalens fokuserbare bor DIREKTE i dens
  // shadow root - ingen slots, ingen indlejrede shadow roots. Kommer der
  // slottet lysindhold i en fremtidig app-version, er shadowRoot.activeElement
  // null mens fokus staar der, og !inside-grenen nedenfor ville traekke fokus
  // tilbage ved hvert Tab. Det er den antagelse her der foerst braekker.
  #trap(event, modal) {
    const root = modal.shadowRoot;
    if (!root) return;
    const items = [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
    if (!items.length) {
      event.preventDefault();
      modal.focus({ preventScroll: true });
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const inside = root.activeElement;
    // Fokus staar paa selve dialogen (eller er sluppet ud): traek det ind.
    if (!inside) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && inside === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && inside === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #release() {
    if (this.onKeydown) {
      document.removeEventListener('keydown', this.onKeydown, true);
      this.onKeydown = null;
    }
    if (this.openObserver) {
      this.openObserver.disconnect();
      this.openObserver = null;
    }
    const modal = this.trapped;
    if (!modal) return;
    this.trapped = null;
    // Rul dialog-rollen tilbage. App'ens host bliver liggende renderet naar den
    // lukker (kun indholdet gaar visibility:hidden), og et element der bliver
    // ved med at sige aria-modal='true' kan faa AT til at holde resten af
    // siden ude af tilgaengelighedstraeet efter foerste aabn/luk.
    modal.removeAttribute('role');
    modal.removeAttribute('aria-modal');
    modal.removeAttribute('aria-label');
    modal.removeAttribute('tabindex');
    if (this.label && this.label.tagName === 'BUTTON' && document.contains(this.label)) {
      this.label.focus();
    }
  }
}

if (!customElements.get('contribe-trigger')) {
  customElements.define('contribe-trigger', ContribeTrigger);
}
